import { reactive, ref, readonly } from 'vue'

/**
 * Cortina de transición (pantalla negra que sube).
 *
 * Dos usos, mismo lenguaje:
 *  - cambio de página (router): la cortina sube y tapa → con la pantalla tapada
 *    la página nueva se lleva al tope con scroll suave → la cortina queda de
 *    fondo negro y el contenido aparece dentro de un recuadro que sube hasta el
 *    tope.
 *  - apertura del menú de navlinks: la cortina sube y tapa → aparece el menú,
 *    cuyo contenido entra dentro del mismo recuadro que sube.
 *
 * Fases: idle · covering (subiendo) · covered (tapa completa) · revealing (el
 * contenido se revela dentro del recuadro que sube)
 */
const DURACION_PAGINA = {
  tapa: 420,     // sube hasta tapar
  salida: 620,   // el recuadro sube hasta el tope revelando el contenido
  topeMax: 900   // espera máxima al scroll suave hasta el tope
}

const DURACION_MENU = {
  tapa: 320,     // abrir el menú tiene que sentirse ágil
  salida: 480
}

const fase = ref('idle')
const origen = ref('page')

/* Duraciones vivas. Se publican como variables CSS en :root para que la
   animación (CSS) y las esperas (JS) no se desincronicen nunca. */
const duraciones = reactive({
  tapa: DURACION_PAGINA.tapa,
  salida: DURACION_PAGINA.salida
})

export const pageCurtainPhase = readonly(fase)
export const curtainOrigin = readonly(origen)
export const pageCurtainDurations = readonly(duraciones)
export { DURACION_MENU }

const prefiereSinMovimiento = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const publicarDuraciones = () => {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  root.style.setProperty('--curtain-tapa', `${duraciones.tapa}ms`)
  root.style.setProperty('--curtain-salida', `${duraciones.salida}ms`)
}

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const esperarArriba = async (limite) => {
  const inicio = Date.now()

  while (window.scrollY > 0 && Date.now() - inicio < limite) {
    await esperar(40)
  }

  // Si el scroll suave no llegó a destino, se cierra el tramo sin animación.
  if (window.scrollY > 0) {
    window.scrollTo(0, 0)
  }
}

let enCurso = false

/**
 * Sólo en navegaciones reales entre páginas distintas:
 * ni en la primera carga, ni al repetir la misma ruta, ni en los cambios de
 * query de la misma página (filtros del catálogo, secciones del panel).
 */
export const deberiaAnimar = (to, from) => {
  if (!from || !from.matched || from.matched.length === 0) return false
  if (!to || !to.matched || to.matched.length === 0) return false
  if (to.fullPath === from.fullPath) return false
  if (to.name && from.name && to.name === from.name) return false

  // Dentro del panel NO se anima: la sidebar y el header quedan fijos y sólo
  // cambia el contenido (la cortina taparía todo el panel, no solo la página).
  if (to.meta?.panel && from.meta?.panel) return false

  return true
}

/**
 * Sube la cortina hasta tapar la pantalla.
 * `origen` define quién se va a revelar dentro del recuadro: 'page' o 'menu'.
 */
export const taparPantalla = async ({ tapa, origen: nuevoOrigen = 'page' } = {}) => {
  if (enCurso) return false

  enCurso = true
  origen.value = nuevoOrigen

  if (tapa) duraciones.tapa = tapa

  publicarDuraciones()

  if (prefiereSinMovimiento()) {
    fase.value = 'covered'

    return true
  }

  fase.value = 'covering'
  await esperar(duraciones.tapa)
  fase.value = 'covered'

  return true
}

/**
 * Deja que el contenido se revele dentro del recuadro que sube hasta el tope.
 * Con `scrollAlTope` (cambios de página) primero lleva la página nueva hasta
 * arriba con scroll suave; el menú lo usa en false para no mover la página en
 * la que estaba el usuario.
 */
export const destaparPantalla = async ({ salida, scrollAlTope = true } = {}) => {
  if (!enCurso) return

  if (salida) duraciones.salida = salida

  publicarDuraciones()

  if (prefiereSinMovimiento()) {
    if (scrollAlTope) window.scrollTo(0, 0)

    fase.value = 'idle'
    enCurso = false

    return
  }

  if (scrollAlTope) {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      window.scrollTo(0, 0)
    }

    await esperarArriba(DURACION_PAGINA.topeMax)
  }

  fase.value = 'revealing'
  await esperar(duraciones.salida)
  fase.value = 'idle'
  enCurso = false
}

/** Deja la cortina en reposo (por ejemplo si la navegación falló). */
export const resetearCortina = () => {
  fase.value = 'idle'
  enCurso = false
}
